import { c as _c } from "react/compiler-runtime";
import type { ChainId, MarketParams } from '@morpho-org/blue-sdk';
import type { SimulationAuthorization } from '@morpho-org/evm-simulation';
import { isDefined } from '@morpho-org/morpho-ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { TransactionBuilder } from '@/contexts/Transaction/TransactionV2.context';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { getSimulate, isSimulationUnsupportedSender, simulationAuthorizationsKey, toSimulationTx, type Transaction } from '@/services/simulate/simulate';
import { resolveSimulatedFinalPosition, type SimulationStatus } from '@/services/simulate/simulatedPosition.helpers';
import { useVerifiedSimulationResult } from '@/services/simulate/useVerifiedSimulationResult';
import { computeLtv } from './marketAction.helpers';
import { buildRefinancePositionReadTxs, decodeRefinancePositionReads } from './marketRefinancePositionReads';
const BLACKLIST_ERROR_CODE = 'BLACKLIST_ERROR';

/** Post-action position of one side (source or target) of a refinance. */
export interface RefinanceSideFinalPosition {
  collateral: bigint | undefined;
  borrowAssets: bigint | undefined;
  ltv: bigint | undefined;
  isUnverifiedFinalPosition: boolean;
}
export interface UseMarketRefinanceSimulatedFinalPositionParams {
  chainId: ChainId;
  sourceMarketParams: MarketParams | undefined;
  targetMarketParams: MarketParams | undefined;
  /** Refinance builder whose unsigned bundle is simulated. */
  transaction: TransactionBuilder | undefined;
  /** Typed preview authorizations (approvals, permits, Blue authorizations) the SDK prepares before the unsigned bundle. */
  simulationAuthorizations?: readonly SimulationAuthorization[];
  /** Stable query-key identity when calldata churns each poll (wall-clock maxSharePrice etc.). */
  simulationKey?: ReadonlyArray<unknown>;
  isPreparing?: boolean;
  areRequirementsReady?: boolean;
  /** Current source position + oracle price. */
  sourceCurrent: {
    collateral: bigint;
    borrowAssets: bigint;
    price: bigint;
  } | undefined;
  /** Current target position + oracle price. */
  targetCurrent: {
    collateral: bigint;
    borrowAssets: bigint;
    price: bigint;
  } | undefined;
  /** JS-math source position-after, shown when the sim is unavailable/unverified. */
  sourceFallback: {
    collateral: bigint;
    borrowAssets: bigint;
  } | undefined;
  /** JS-math target position-after. */
  targetFallback: {
    collateral: bigint;
    borrowAssets: bigint;
  } | undefined;
}

/**
 * Refinance sister of {@link useMarketSimulatedFinalPosition}: reads the post-refinance
 * Collateral / Loan of BOTH the source and target markets straight from the simulated bundle
 * ("Option B"), by appending `position`/`market` staticcalls for the two markets
 * ({@link buildRefinancePositionReadTxs}) and decoding their `returnData`
 * ({@link decodeRefinancePositionReads}). Falls back to the JS-math position-after per side, with
 * an estimate badge, when the simulation is unavailable/unverified or the reads can't be decoded.
 * LTV is recomputed per side from the resolved collateral/loan + the pre-fetched oracle price.
 */
export function useMarketRefinanceSimulatedFinalPosition(t0) {
  const $ = _c(68);
  const {
    chainId,
    sourceMarketParams,
    targetMarketParams,
    transaction,
    simulationAuthorizations,
    simulationKey,
    isPreparing: t1,
    areRequirementsReady: t2,
    sourceCurrent,
    targetCurrent,
    sourceFallback,
    targetFallback
  } = t0;
  const isPreparing = t1 === undefined ? false : t1;
  const areRequirementsReady = t2 === undefined ? true : t2;
  const {
    account
  } = useWeb3Context();
  const userAddress = account.address;
  const {
    evmPreviewSimulation: isSimulationEnabled
  } = useFeatureFlags();
  let t3;
  if ($[0] !== chainId || $[1] !== sourceMarketParams || $[2] !== targetMarketParams || $[3] !== userAddress) {
    t3 = sourceMarketParams && targetMarketParams && userAddress ? buildRefinancePositionReadTxs({
      chainId,
      sourceMarketParams,
      targetMarketParams,
      user: userAddress
    }) : undefined;
    $[0] = chainId;
    $[1] = sourceMarketParams;
    $[2] = targetMarketParams;
    $[3] = userAddress;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const readTxs = t3;
  let t4;
  bb0: {
    if (!transaction || !userAddress) {
      t4 = undefined;
      break bb0;
    }
    let t5;
    if ($[5] !== transaction || $[6] !== userAddress) {
      t5 = toSimulationTx(userAddress, transaction.buildTx());
      $[5] = transaction;
      $[6] = userAddress;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    const finalTx = t5;
    let t6;
    if ($[8] !== finalTx || $[9] !== readTxs) {
      t6 = readTxs ? [finalTx, ...readTxs] : [finalTx];
      $[8] = finalTx;
      $[9] = readTxs;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    t4 = t6;
  }
  const transactions = t4;
  let t5;
  if ($[11] !== account || $[12] !== chainId || $[13] !== isSimulationEnabled || $[14] !== userAddress) {
    t5 = isSimulationEnabled && isDefined(userAddress) && !account.isWrongChain && !isSimulationUnsupportedSender(chainId, account);
    $[11] = account;
    $[12] = chainId;
    $[13] = isSimulationEnabled;
    $[14] = userAddress;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const isSimulationIntended = t5;
  let t6;
  if ($[16] !== areRequirementsReady || $[17] !== isPreparing || $[18] !== isSimulationIntended || $[19] !== transactions) {
    t6 = isSimulationIntended && !isPreparing && areRequirementsReady && isDefined(transactions);
    $[16] = areRequirementsReady;
    $[17] = isPreparing;
    $[18] = isSimulationIntended;
    $[19] = transactions;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const canFetch = t6;
  let t7;
  if ($[21] !== simulationAuthorizations) {
    t7 = simulationAuthorizationsKey(simulationAuthorizations);
    $[21] = simulationAuthorizations;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const prefixSignature = t7;
  const t8 = sourceMarketParams?.id;
  const t9 = targetMarketParams?.id;
  let t10;
  if ($[23] !== simulationKey || $[24] !== transactions) {
    t10 = simulationKey ?? transactions?.at(-1);
    $[23] = simulationKey;
    $[24] = transactions;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== chainId || $[27] !== prefixSignature || $[28] !== t10 || $[29] !== t8 || $[30] !== t9 || $[31] !== userAddress) {
    t11 = ["market-refinance-simulated-final-position", chainId, userAddress, t8, t9, t10, prefixSignature];
    $[26] = chainId;
    $[27] = prefixSignature;
    $[28] = t10;
    $[29] = t8;
    $[30] = t9;
    $[31] = userAddress;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== chainId || $[34] !== simulationAuthorizations || $[35] !== transactions) {
    t12 = () => getSimulate({
      chainId,
      transactions,
      mode: "preview",
      authorizations: simulationAuthorizations
    });
    $[33] = chainId;
    $[34] = simulationAuthorizations;
    $[35] = transactions;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== canFetch || $[38] !== t11 || $[39] !== t12) {
    t13 = {
      queryKey: t11,
      queryFn: t12,
      enabled: canFetch,
      retry: false,
      staleTime: 15000,
      gcTime: 60000,
      placeholderData: keepPreviousData
    };
    $[37] = canFetch;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const {
    data,
    error,
    isPlaceholderData,
    isFetching
  } = useQuery(t13);
  const isSettled = !isPreparing && areRequirementsReady;
  let t14;
  if ($[41] !== data || $[42] !== error || $[43] !== isSettled) {
    t14 = {
      data,
      error,
      isSettled
    };
    $[41] = data;
    $[42] = error;
    $[43] = isSettled;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const {
    hasVerifiedResult,
    hasSettledResult
  } = useVerifiedSimulationResult(t14);
  let t15;
  bb1: {
    if (!isSimulationIntended) {
      t15 = "disabled";
      break bb1;
    }
    if (hasVerifiedResult && hasSettledResult) {
      t15 = "success";
      break bb1;
    }
    if (!isSettled) {
      t15 = "loading";
      break bb1;
    }
    if (!isDefined(transactions)) {
      t15 = "disabled";
      break bb1;
    }
    if (isPlaceholderData) {
      t15 = "loading";
      break bb1;
    }
    if (error) {
      t15 = "error";
      break bb1;
    }
    if (data?.code === BLACKLIST_ERROR_CODE) {
      t15 = "blacklist";
      break bb1;
    }
    if (data) {
      t15 = "error";
      break bb1;
    }
    t15 = "loading";
  }
  const status = t15;
  const isRefreshing = isSimulationIntended && (!isSettled || isFetching || isPlaceholderData);
  let t16;
  bb2: {
    if (!data?.success || !sourceMarketParams || !targetMarketParams || !readTxs || !transactions) {
      t16 = undefined;
      break bb2;
    }
    let t17;
    if ($[45] !== data.calls || $[46] !== sourceMarketParams || $[47] !== targetMarketParams || $[48] !== transactions.length) {
      t17 = decodeRefinancePositionReads({
        calls: data.calls,
        expectedCallCount: transactions.length,
        sourceMarketParams,
        targetMarketParams
      });
      $[45] = data.calls;
      $[46] = sourceMarketParams;
      $[47] = targetMarketParams;
      $[48] = transactions.length;
      $[49] = t17;
    } else {
      t17 = $[49];
    }
    t16 = t17;
  }
  const simulated = t16;
  let t17;
  if ($[50] !== status) {
    t17 = (current, simulatedSide, fallback) => {
      const collateral = resolveSimulatedFinalPosition({
        status,
        simulatedValue: simulatedSide?.collateral,
        fallbackPosition: fallback?.collateral
      });
      const loan = resolveSimulatedFinalPosition({
        status,
        simulatedValue: simulatedSide?.borrowAssets,
        fallbackPosition: fallback?.borrowAssets
      });
      const ltv = isDefined(collateral.displayedFinalPosition) && isDefined(loan.displayedFinalPosition) && current ? computeLtv({
        collateral: collateral.displayedFinalPosition,
        borrowAssets: loan.displayedFinalPosition,
        price: current.price
      }) : undefined;
      return {
        collateral: collateral.displayedFinalPosition,
        borrowAssets: loan.displayedFinalPosition,
        ltv,
        isUnverifiedFinalPosition: collateral.isUnverifiedFinalPosition
      };
    };
    $[50] = status;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  const resolveSide = t17;
  const t18 = simulated?.source;
  let t19;
  if ($[52] !== resolveSide || $[53] !== sourceCurrent || $[54] !== sourceFallback || $[55] !== t18) {
    t19 = resolveSide(sourceCurrent, t18, sourceFallback);
    $[52] = resolveSide;
    $[53] = sourceCurrent;
    $[54] = sourceFallback;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const t20 = simulated?.target;
  let t21;
  if ($[57] !== resolveSide || $[58] !== t20 || $[59] !== targetCurrent || $[60] !== targetFallback) {
    t21 = resolveSide(targetCurrent, t20, targetFallback);
    $[57] = resolveSide;
    $[58] = t20;
    $[59] = targetCurrent;
    $[60] = targetFallback;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  const t22 = status === "loading";
  let t23;
  if ($[62] !== isRefreshing || $[63] !== status || $[64] !== t19 || $[65] !== t21 || $[66] !== t22) {
    t23 = {
      source: t19,
      target: t21,
      status,
      isLoading: t22,
      isRefreshing
    };
    $[62] = isRefreshing;
    $[63] = status;
    $[64] = t19;
    $[65] = t21;
    $[66] = t22;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  return t23;
}